import { keyBy, mean, uniq } from "lodash";

import { type User } from "@internal/user";
import {
  type GetOverviewMetricsReport,
  type GetOverviewMetricsReport_me_overviewMetricsReport_rows,
  type SalesPageMetric,
} from "@north-beam/nb-client-side";
import { Currencies, type EcomAccountingSettings } from "@north-beam/nb-common";

import { WIZARD_STEPS } from "./constants";
import {
  type BaselineMetrics,
  type Cogs,
  type DaySelection,
  type MetricPointWithDate,
  type ProfitableDayThreshold,
  type WizardStep,
} from "./types";

export function adjustMetricsGrossRev(
  rows: any[],
  ecomAccountingSettings: EcomAccountingSettings,
): MetricPointWithDate[] {
  return rows.map((row) =>
    adjustMetricsGrossRevRow(row, ecomAccountingSettings),
  );
}

export function adjustMetricsGrossRevRow(
  row: any,
  ecomAccountingSettings: EcomAccountingSettings,
): MetricPointWithDate {
  const convs = row.convs.map((conv: any) => {
    let rev = conv.rev ?? 0;
    let ftRev = conv.ftRev ?? 0;
    let rtnRev = conv.rtnRev ?? 0;

    if (ecomAccountingSettings.removeQuotedTaxFromTopline) {
      rev += conv.taxValue ?? 0;
      ftRev += conv.ftTaxValue ?? 0;
      rtnRev += conv.rtnTaxValue ?? 0;
    }

    if (ecomAccountingSettings.removeQuotedShippingFromTopline) {
      rev += conv.shippingValue ?? 0;
      ftRev += conv.ftShippingValue ?? 0;
      rtnRev += conv.rtnShippingValue ?? 0;
    }

    if (ecomAccountingSettings.removeRefundedValueFromTopline) {
      rev += conv.orderRefundedValue ?? 0;
      ftRev += conv.ftOrderRefundedValue ?? 0;
      rtnRev += conv.rtnOrderRefundedValue ?? 0;
    }

    if (!ecomAccountingSettings.addOrderDiscountsToTopline) {
      rev += conv.orderDiscounts ?? 0;
      ftRev += conv.ftOrderDiscounts ?? 0;
      rtnRev += conv.rtnOrderDiscounts ?? 0;
    }

    return { ...conv, rev };
  });

  return { ...row, convs };
}

export function normalizeMetricValue(value: number) {
  if (!isFinite(value)) {
    return 0;
  }

  if (isNaN(value)) {
    return 0;
  }

  return value;
}

export function resolveBaseMetricsForGrossRev(metrics: string[]) {
  const baseMetrics = [...metrics];

  if (metrics.includes("rev")) {
    baseMetrics.push(
      "taxValue",
      "shippingValue",
      "orderRefundedValue",
      "orderDiscounts",
    );
  }

  if (metrics.includes("ftRev")) {
    baseMetrics.push(
      "ftTaxValue",
      "ftShippingValue",
      "ftOrderRefundedValue",
      "ftOrderDiscounts",
    );
  }

  if (metrics.includes("rtnRev")) {
    baseMetrics.push(
      "rtnTaxValue",
      "rtnShippingValue",
      "rtnOrderRefundedValue",
      "rtnOrderDiscounts",
    );
  }

  return uniq(baseMetrics);
}

export function adjustOverviewReportGrossRev(
  data: GetOverviewMetricsReport,
  ecomAccountingSettings: EcomAccountingSettings,
) {
  const rows = data.me.overviewMetricsReport.rows.map((row) => {
    return {
      ...row,
      metrics: adjustMetricsGrossRev(row.metrics, ecomAccountingSettings),
    };
  });

  return { ...data.me.overviewMetricsReport, rows };
}

export function parseNumber(val: string) {
  const num = Number(val.replaceAll(",", ""));
  return isNaN(num) ? 0 : num;
}

export function sumCogs(cogs: Cogs) {
  return (
    cogs.spend.value +
    cogs.otherMarketingCosts.value +
    cogs.discounts.value +
    cogs.shipping.value +
    cogs.taxes.value +
    cogs.returns.value +
    cogs.productCosts.value +
    cogs.otherCosts.value
  );
}

export function sumContributionMargin(cogs: Cogs) {
  return cogs.spend.value + cogs.otherMarketingCosts.value;
}

export function percentile(arr: number[], q: number) {
  const quartile = q / 100;

  const data = [...arr].sort((a, b) => a - b);

  const p = (data.length - 1) * quartile;
  const b = Math.floor(p);

  const remainder = p - b;

  if (data[b + 1] !== undefined) {
    return data[b] + remainder * (data[b + 1] - data[b]);
  } else {
    return data[b];
  }
}

export function getCompletedWizardSteps(step: WizardStep) {
  const result: WizardStep[] = [];
  const activeIndex = WIZARD_STEPS.indexOf(step);

  for (const [idx, s] of WIZARD_STEPS.entries()) {
    if (idx < activeIndex) {
      result.push(s);
    }
  }

  return result;
}

export function getNumberInputIconForMetric(
  user: User,
  metric: SalesPageMetric | undefined,
) {
  if (!metric) {
    return;
  }

  if (metric.format === "dollars") {
    return Currencies.getAttribute(user.currencies.currentCurrency, "symbol");
  }

  if (metric.format === "percentage") {
    return "%";
  }
}

function calculateCancelledOrderValuesPerOrder(
  baselineMetrics: BaselineMetrics,
  baselineDays: any[],
  cancelledOrders: any[],
  method: ProfitableDayThreshold["method"],
) {
  const baselineDaysByDate = keyBy(baselineDays, "date");

  return cancelledOrders.map((row) => {
    const rowMetrics = baselineDaysByDate[row.localdate];
    const txns = rowMetrics ? baselineMetrics.txns.calculate(rowMetrics) : 0;
    let value = 0;

    if (method === "blended") {
      value = row.cancelledValue;
    }

    if (method === "first-time") {
      value = row.cancelledValueFirstTime;
    }

    if (method === "returning") {
      value = row.cancelledValueReturning;
    }

    return txns === 0 ? 0 : value / txns;
  });
}

export function calculateBaselineDaysAverages({
  baselineDays,
  cancelledOrders,
  baselineMetrics,
  ecomAccountingSettings,
  profitableDayThresholdMethod,
}: {
  baselineDays: MetricPointWithDate[];
  cancelledOrders: MetricPointWithDate[];
  baselineMetrics: BaselineMetrics;
  ecomAccountingSettings: EcomAccountingSettings;
  profitableDayThresholdMethod: ProfitableDayThreshold["method"];
}) {
  let txnsMetric = baselineMetrics.txns;
  let orderDiscountsMetric = baselineMetrics.orderDiscounts;
  let shippingValueMetric = baselineMetrics.shippingValue;
  let taxValueMetric = baselineMetrics.taxValue;
  let orderRefundedValueMetric = baselineMetrics.orderRefundedValue;

  if (profitableDayThresholdMethod === "first-time") {
    txnsMetric = baselineMetrics.ftTxns;
    orderDiscountsMetric = baselineMetrics.ftOrderDiscounts;
    shippingValueMetric = baselineMetrics.ftShippingValue;
    taxValueMetric = baselineMetrics.ftTaxValue;
    orderRefundedValueMetric = baselineMetrics.ftOrderRefundedValue;
  }

  if (profitableDayThresholdMethod === "returning") {
    txnsMetric = baselineMetrics.rtnTxns;
    orderDiscountsMetric = baselineMetrics.rtnOrderDiscounts;
    shippingValueMetric = baselineMetrics.rtnShippingValue;
    taxValueMetric = baselineMetrics.rtnTaxValue;
    orderRefundedValueMetric = baselineMetrics.rtnOrderRefundedValue;
  }

  const normalizedValue = (val: number) => {
    return isFinite(val) && !isNaN(val) ? val : 0;
  };

  const gross = adjustMetricsGrossRev(baselineDays, ecomAccountingSettings);

  const averageSpend =
    mean(
      gross.map((row) =>
        normalizedValue(
          baselineMetrics.spend.calculate(row) /
            baselineMetrics.txns.calculate(row),
        ),
      ),
    ) || 0;

  const averageDiscount =
    mean(
      gross.map((row) =>
        normalizedValue(
          orderDiscountsMetric.calculate(row) / txnsMetric.calculate(row),
        ),
      ),
    ) || 0;

  const averageShipping =
    mean(
      gross.map((row) =>
        normalizedValue(
          shippingValueMetric.calculate(row) / txnsMetric.calculate(row),
        ),
      ),
    ) || 0;

  const averageTaxes =
    mean(
      gross.map((row) =>
        normalizedValue(
          taxValueMetric.calculate(row) / txnsMetric.calculate(row),
        ),
      ),
    ) || 0;

  const averageRefunds =
    mean(
      gross.map((row) =>
        normalizedValue(
          orderRefundedValueMetric.calculate(row) / txnsMetric.calculate(row),
        ),
      ),
    ) || 0;

  const averageCancellations =
    mean(
      calculateCancelledOrderValuesPerOrder(
        baselineMetrics,
        gross,
        cancelledOrders,
        profitableDayThresholdMethod,
      ),
    ) || 0;

  return {
    averageSpend,
    averageDiscount,
    averageShipping,
    averageTaxes,
    averageRefunds,
    averageCancellations,
  };
}

export function calculateMeanValueForDaysSelection({
  daysSelection,
  metric,
  rows,
}: {
  daysSelection: DaySelection[];
  metric?: SalesPageMetric;
  rows?: GetOverviewMetricsReport_me_overviewMetricsReport_rows[];
}) {
  if (!rows || !metric) {
    return 0;
  }

  const firstRow = rows[0];

  if (!firstRow) {
    return 0;
  }

  const excludedDays = daysSelection
    .filter((e) => e.isExcluded || e.willBeExcluded)
    .map((e) => e.day);

  const values = [];

  for (const metricPoint of firstRow.metrics) {
    if (excludedDays.includes(metricPoint.date)) {
      continue;
    }

    values.push(normalizeMetricValue(metric.calculate(metricPoint)));
  }

  return mean(values);
}

export function roundNumberTwoDecimalPlaces(num: number) {
  return Math.round(num * 100) / 100;
}
